import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import axios from "axios";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const API_BASE = import.meta.env?.VITE_API_BASE;
export const AI_API_BASE = import.meta.env?.VITE_AI_API_BASE;
export const AI_API_KEY = import.meta.env?.VITE_AI_API_KEY;
export const AI_API_KEY_V1 = import.meta.env?.VITE_AI_API_KEY_V1;
export const ADAPTATION_API_KEY = import.meta.env?.VITE_ADAPTATION_API_KEY;
export const USE_OPENAI_PROXY = true;

export const fetchSigGeoJson = async (
  period: string,
  scenario: string,
  season: string,
  variable: string,
) => {
  const apiBase = `${API_BASE}/api/v1/netcdf`;
  const params = `?scenario=${scenario}&period=${period}&season=${season}&variable=${variable}`;
  return axios.get(`${apiBase}/geojson${params}`);
};

export const fetchGeoTiffUrl = async (
  period: string,
  scenario: string,
  season: string,
  variable: string,
) => {
  const apiBase = `${API_BASE}/api/v1/netcdf`;
  const params = `?scenario=${scenario}&period=${period}&season=${season}&variable=${variable}`;
  return axios.get(`${apiBase}/geotiff${params}`);
};

export const fetchHistGeoTiffUrl = async (
  period: string,
  scenario: string,
  season: string,
  variable: string,
) => {
  const apiBase = `${API_BASE}/api/v1/netcdf`;
  const params = `?scenario=${scenario}&period=${period}&season=${season}&variable=${variable}`;
  return axios.get(`${apiBase}/hist-geotiff${params}`);
};

export const fetchZoneData = async () => {
  try {
    const mod: any = await import("../assets/tz_shp_zone.geojson?raw");
    const text = mod?.default ?? mod;
    if (typeof text === "string") {
      return JSON.parse(text);
    }
  } catch {}
  const base = (import.meta as any)?.env?.BASE_URL || "/";
  const baseNorm = String(base).endsWith("/")
    ? String(base)
    : String(base) + "/";
  const url = `${baseNorm}assets/data/tz_shp_zone.geojson`;
  const { data } = await axios.get(url);
  return data;
};

// Adaptation API: dynamic crop categories and crops
export const fetchCropCategories = async () => {
  const url = `${API_BASE}/api/v1/projections/crop-categories/`;
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${ADAPTATION_API_KEY}`,
      Accept: "application/json",
    },
  });
  return data as Array<{
    id: number;
    name: string;
    description?: string;
    crop_count?: number;
  }>;
};

export const fetchCrops = async () => {
  const url = `${API_BASE}/api/v1/projections/crops/`;
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${ADAPTATION_API_KEY}`,
      Accept: "application/json",
    },
  });
  return data as Array<{
    id: number;
    name: string;
    category: number;
    category_name: string;
  }>;
};

export const fetchPeriods = async () => {
  const url = `${API_BASE}/api/v1/projections/periods/`;
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${ADAPTATION_API_KEY}`,
      Accept: "application/json",
    },
  });
  return data as Array<{
    id: number;
    code: string;
    name: string;
    start_year: number;
    end_year: number;
  }>;
};

export const fetchRegions = async () => {
  const url = `${API_BASE}/api/v1/projections/regions/`;
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${ADAPTATION_API_KEY}`,
      Accept: "application/json",
    },
  });
  return data as Array<{
    id: number;
    name: string;
    climate_zone: number;
    climate_zone_name: string;
  }>;
};

export const fetchClimateZones = async () => {
  const url = `${API_BASE}/api/v1/projections/climate-zones/`;
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${ADAPTATION_API_KEY}`,
      Accept: "application/json",
    },
  });
  return data as Array<{ id: number; name: string; code?: string }>;
};

export const fetchStressors = async () => {
  const url = `${API_BASE}/api/v1/projections/stressors/`;
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${ADAPTATION_API_KEY}`,
      Accept: "application/json",
    },
  });
  return data as Array<{ id: number; code: string; name: string }>;
};

export const fetchScenarios = async () => {
  const url = `${API_BASE}/api/v1/projections/scenarios/`;
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${ADAPTATION_API_KEY}`,
      Accept: "application/json",
    },
  });
  return data as Array<{ id: number; code: string; name: string }>;
};

export const fetchAllData = async () => {
  const url = `${API_BASE}/api/v1/projections/analytics/all-data/`;
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${ADAPTATION_API_KEY}`,
      Accept: "application/json",
    },
  });
  return data as any;
};

export const fetchProjections = async (params?: {
  region?: number;
  crop?: number;
  scenario?: string;
  period?: string;
}) => {
  const url = `${API_BASE}/api/v1/projections/projections/`;
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${ADAPTATION_API_KEY}`,
      Accept: "application/json",
    },
    params,
  });
  return data as Array<{
    id: number;
    region?: string;
    region_name?: string;
    climate_zone?: string;
    crop?: string;
    crop_name?: string;
    crop_category?: string;
    scenario?: string;
    scenario_code?: string;
    period?: string;
    period_code?: string;
    risk_change?: number;
    risk_severity?: number;
    risk_category?: string;
    yield_change?: number;
    yield_impact?: string;
    dominant_stressors?: any[];
  }>;
};

export const fetchProjectionById = async (id: number) => {
  const url = `${API_BASE}/api/v1/projections/projections/${id}/`;
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${ADAPTATION_API_KEY}`,
      Accept: "application/json",
    },
  });
  return data as any;
};

export const fetchHighRiskAnalytics = async (params?: {
  region?: number;
  crop?: number;
  scenario?: string;
  period?: string;
}) => {
  const url = `${API_BASE}/api/v1/projections/analytics/high-risk/`;
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${ADAPTATION_API_KEY}`,
      Accept: "application/json",
    },
    params,
  });
  return data as Array<{
    region?: string;
    crop?: string;
    scenario?: string;
    period?: string;
    risk_change: number;
    yield_change: number;
    risk_category: string;
  }>;
};

export const fetchAdaptationOptionsAnalytics = async (params: {
  region: number;
  crop: number;
  scenario: string;
  period?: string;
}) => {
  const url = `${API_BASE}/api/v1/projections/analytics/adaptation-options/`;
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${ADAPTATION_API_KEY}`,
      Accept: "application/json",
    },
    params,
  });
  return data as Array<{
    region__name: string;
    crop__name: string;
    scenario__code: string;
    period__code: string;
    adaptation_options: string;
    risk_change?: number;
    yield_change?: number;
  }>;
};

export const fetchHierarchicalAnalytics = async (params?: {
  hierarchy?: string;
  include_projections?: boolean;
}) => {
  const url = `${API_BASE}/api/v1/projections/analytics/hierarchical/`;
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${ADAPTATION_API_KEY}`,
      Accept: "application/json",
    },
    params,
  });
  return data as any;
};

export const fetchTimeSeriesData = async (
  variable: string,
  coords: number[] | null,
  bbox: number[] | null,
  scale: "seasonal" | "annual" | "monthly_spread" = "seasonal",
) => {
  return axios.post(`${API_BASE}/api/v1/netcdf/timeseries/?scale=${scale}`, {
    variable,
    coords,
    bbox,
  });
};

// Functions for indices data
export const fetchIndicesSigGeoJson = async (
  period: string,
  scenario: string,
  index: string,
) => {
  const apiBase = `${API_BASE}/api/v1/indices`;
  const params = `?scenario=${scenario}&period=${period}&index=${index}`;
  return axios.get(`${apiBase}/geojson${params}`);
};

export const fetchIndicesGeoTiffUrl = async (
  period: string,
  scenario: string,
  index: string,
) => {
  const apiBase = `${API_BASE}/api/v1/indices`;
  const params = `?scenario=${scenario}&period=${period}&index=${index}`;
  return axios.get(`${apiBase}/geotiff${params}`);
};

export const fetchIndicesHistGeoTiffUrl = async (
  period: string,
  scenario: string,
  index: string,
) => {
  const apiBase = `${API_BASE}/api/v1/indices`;
  const params = `?scenario=${scenario}&period=${period}&index=${index}`;
  return axios.get(`${apiBase}/hist-geotiff${params}`);
};

export const fetchIndicesTimeSeriesData = async (
  index: string,
  coords: number[] | null,
  bbox: number[] | null,
  scale: "seasonal" | "annual" | "monthly_spread" = "seasonal",
) => {
  return axios.post(`${API_BASE}/api/v1/indices/timeseries/?scale=${scale}`, {
    index,
    coords,
    bbox,
  });
};

// Functions for crops data
export const fetchCropsTimeSeriesData = async (
  product: string,
  coords: number[] | null,
  bbox: number[] | null,
  scale: "seasonal" | "annual" | "monthly_spread" = "seasonal",
) => {
  return axios.post(
    `${API_BASE}/api/v1/impacts/cwd/timeseries/?scale=${scale}`,
    {
      crop: product,
      coords,
      bbox,
    },
  );
};

export const fetchCWDGeoTiffUrl = async (
  period: string,
  scenario: string,
  crop: string,
) => {
  const apiBase = `${API_BASE}/api/v1/impacts`;
  const params = `?scenario=${scenario}&period=${period}&crop=${crop}`;
  return axios.get(`${apiBase}/cwd/geotiff${params}`);
};

export const fetchHistoricalCWDGeoTiffUrl = async (crop: string) => {
  const apiBase = `${API_BASE}/api/v1/impacts`;
  const params = `?scenario=historical&period=1985_2014&crop=${crop}`;
  return axios.get(`${apiBase}/cwd/hist-geotiff${params}`);
};

export type AIReponse = {
  summary: {
    key_insights: string;
    recommendations: string[];
    risks: string[];
    text_color: string;
  };
  decision: {
    value: string;
    color: string;
  };
  confidence: number;
  actions: string[];
  recommendations?: string[]; // Pour compatibilité avec l'ancien format
  risks?: string[]; // Pour compatibilité avec l'ancien format
  key_findings?: string[]; // Pour compatibilité avec climate-impacts-summary
};

export type AIReponseV1 = {
  summary: string;
  decision: {
    value: string;
    color: string;
  };
  confidence: number;
  text_color?: string;
  key_insights?: string;
  actions: string[];
  recommendations?: string[]; // Pour compatibilité avec l'ancien format
  risks?: string[]; // Pour compatibilité avec l'ancien format
  key_findings?: string[]; // Pour compatibilité avec climate-impacts-summary
};

export const fetchAIResponseHistorical = async (
  model: string,
  jsonFile?: File | null,
  context?: any,
  image?: File,
  stream: boolean = false,
) => {
  try {
    // Utiliser FormData selon la documentation de l'API
    const formData = new FormData();
    formData.append("model", model);

    // Ajouter le fichier JSON (obligatoire) - utiliser 'geojson' selon l'API
    if (jsonFile) {
      formData.append("geojson", jsonFile);
    }

    // Ajouter le context si fourni (optionnel)
    if (context) {
      const contextData =
        typeof context === "string" ? context : JSON.stringify(context);
      formData.append("context", contextData);
    }

    // Ajouter l'image si fournie (optionnel)
    if (image) {
      formData.append("image", image);
    }

    // Afficher tous les champs du FormData
    console.log("🔍 [DEBUG] Champs FormData:");
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(
          `  ${key}: File(${value.name}, ${(value.size / 1024).toFixed(2)} KB)`,
        );
      } else {
        console.log(
          `  ${key}: ${
            typeof value === "string"
              ? value.substring(0, 100) + (value.length > 100 ? "..." : "")
              : value
          }`,
        );
      }
    }

    const token = AI_API_KEY_V1 || AI_API_KEY;
    const response = await axios.post<AIReponseV1>(
      `${AI_API_BASE}/interpret?stream=${stream}&structured_output=true`,
      formData,
      {
        headers: token
          ? { Authorization: `Bearer ${token}`, "X-Api-key": token }
          : undefined,
      },
    );

    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchOpenAIProxyNarrative = async (
  context: string,
  model = "gpt-4o-mini",
) => {
  const url =
    (import.meta as any)?.env?.VITE_OPENAI_PROXY_URL || "/api/openai/interpret";
  const { data } = await axios.post(url, { context, model });
  return data;
};

export const fetchAIResponse = async (
  model: string,
  jsonFile?: File | null,
  context?: any,
  image?: File,
  stream: boolean = false,
) => {
  try {
    const formData = new FormData();
    formData.append("model", model);
    if (jsonFile) {
      formData.append("geojson", jsonFile);
    }
    if (context) {
      const ctx =
        typeof context === "string" ? context : JSON.stringify(context);
      formData.append("context", ctx);
    }
    if (image) {
      formData.append("image", image);
    }
    const token = AI_API_KEY_V1 || AI_API_KEY;
    const headers = token
      ? { Authorization: `Bearer ${token}`, "X-Api-key": token }
      : undefined;
    return await axios.post(
      `${AI_API_BASE}/interpret?stream=${stream}&structured_output=true`,
      formData,
      { headers },
    );
  } catch (error: any) {
    throw error;
  }
};

export const fetchAIResponseV2 = async (
  context: any,
  model: string,
  image?: File,
  geojson1?: File,
  geojson2?: File,
  stream: boolean = false,
) => {
  // Création du FormData pour l'envoi multipart (fichiers + données)
  const formData = new FormData();
  formData.append("model", model);
  if (image) {
    formData.append("image", image);
  }
  if (geojson1) {
    formData.append("geojson_first_scenario", geojson1);
  }
  if (geojson2) {
    formData.append("geojson_second_scenario", geojson2);
  }

  formData.append("name_first_scenario", "ssp245");
  formData.append("name_second_scenario", "ssp585");
  formData.append("context", context);

  return axios.post<AIReponse>(
    `${AI_API_BASE}/v2/interpret?stream=${stream}&structured_output=true`,
    formData,
    {
      headers: {
        "Content-Type":
          "multipart/form-data; boundary=----WebKitFormBoundaryOGrLj8JKcBtfkOO8",
        "X-Api-key": AI_API_KEY,
      },
    },
  );
};

export const getVariableNameString = (variable: string): string => {
  switch (variable) {
    case "tasmax":
      return "Maximum Temperature";
    case "tasmin":
      return "Minimum Temperature";
    case "pr":
      return "Precipitation";
    case "tasmean":
      return "Mean Temperature";
    default:
      return titleCase(variable) ?? variable;
  }
};

export const capitalize = (text?: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

export const titleCase = (text?: string) =>
  text
    ? text
        .split(" ")
        .map((word) => capitalize(word))
        .join(" ")
    : text;

export const PERIOD_YEARS: Record<string, string> = {
  near_future: "2025-2054",
  far_future: "2055-2084",
  historical: "1985-2014",
};
